/**
 * AntD design tokens — Microsoft Fluent / 365 look.
 *
 * Translated from the MUI `createTheme` inspiration into AntD tokens (this
 * app is Ant Design, not MUI). One token set per mode; the algorithm swaps
 * at runtime in DarkModeProvider.
 *
 *   Light → Microsoft Blue (#0078D4) on the M365 grey canvas (#F3F2F1),
 *           white paper/cards/sider.
 *   Dark  → Fluent active blue (#2899FF) on the deep-dark canvas (#111111),
 *           #1F1F1F Fluent "elevation" for cards/sider.
 *
 * Segoe UI throughout (Microsoft's system face) and the 4px Fluent radius.
 * The brand wordmark in AppShell keeps its own Palapa Neue font.
 */

import { theme, type ThemeConfig } from "antd";

const MS = {
  light: {
    primary: "#0078D4", // Official Microsoft Blue
    primaryHover: "#4FA6E8",
    primaryActive: "#005A9E",
    bgLayout: "#F3F2F1", // M365 light grey canvas
    bgContainer: "#FFFFFF", // paper / cards / sider
    border: "#E1DFDD", // Fluent neutral stroke
    selectedBg: "rgba(0, 120, 212, 0.10)",
    hoverBg: "rgba(0, 0, 0, 0.04)",
    // Fluent depth ramp: tertiary = resting (cards), secondary = raised
    // (dropdowns/popovers), shadow = flyout (modals/drawers).
    shadowTertiary: "0 1px 2px rgba(0,0,0,0.06), 0 0 1px rgba(0,0,0,0.10)",
    shadowSecondary: "0 2px 4px rgba(0,0,0,0.08), 0 1px 8px rgba(0,0,0,0.10)",
    shadow: "0 4px 8px rgba(0,0,0,0.10), 0 2px 16px rgba(0,0,0,0.12)",
    focusRing: "rgba(0, 120, 212, 0.30)",
  },
  dark: {
    primary: "#2899FF", // Fluent dark active blue
    primaryHover: "#60B2FF",
    primaryActive: "#0066B3",
    bgLayout: "#111111", // deep-dark canvas
    bgContainer: "#1F1F1F", // Fluent elevation (cards / sider)
    text: "#FFFFFF",
    textSecondary: "#A19F9D",
    border: "#292929",
    selectedBg: "rgba(40, 153, 255, 0.16)",
    hoverBg: "rgba(255, 255, 255, 0.06)",
    // Heavier on the dark canvas so depth still reads.
    shadowTertiary: "0 1px 2px rgba(0,0,0,0.32), 0 0 1px rgba(0,0,0,0.44)",
    shadowSecondary: "0 2px 6px rgba(0,0,0,0.40), 0 1px 12px rgba(0,0,0,0.44)",
    shadow: "0 4px 12px rgba(0,0,0,0.48), 0 2px 20px rgba(0,0,0,0.52)",
    focusRing: "rgba(40, 153, 255, 0.36)",
  },
} as const;

const SEGOE = '"Segoe UI", "Helvetica Neue", Arial, sans-serif';

export function buildTheme(mode: "light" | "dark"): ThemeConfig {
  const p = MS[mode];
  return {
    algorithm: mode === "dark" ? theme.darkAlgorithm : theme.defaultAlgorithm,
    cssVar: true,
    hashed: true,
    token: {
      colorPrimary: p.primary,
      colorPrimaryHover: p.primaryHover,
      colorPrimaryActive: p.primaryActive,
      colorInfo: p.primary,
      colorBgLayout: p.bgLayout,
      colorBgContainer: p.bgContainer,
      colorBorder: p.border,
      colorBorderSecondary: p.border,
      ...(mode === "dark"
        ? { colorText: MS.dark.text, colorTextSecondary: MS.dark.textSecondary }
        : {}),
      borderRadius: 4, // Fluent Design standard
      borderRadiusLG: 8,
      fontFamily: SEGOE,
      fontSize: 14,
      // Fluent elevation + focus ring.
      boxShadow: p.shadow,
      boxShadowSecondary: p.shadowSecondary,
      boxShadowTertiary: p.shadowTertiary,
      controlOutline: p.focusRing,
      controlOutlineWidth: 2,
    },
    components: {
      Layout: {
        headerBg: p.bgContainer,
        siderBg: p.bgContainer,
        bodyBg: p.bgLayout,
      },
      Menu: {
        // Transparent item backgrounds so the sider colour shows through
        // uniformly. Without this, AntD's `theme="dark"` Menu paints its
        // legacy dark-blue palette (#001529 family) over our #1F1F1F sider —
        // a dark-blue band for the menu and a darker-blue band for the open
        // submenu children. We keep `theme="dark"` (for light text) but null
        // out the backgrounds.
        itemBg: "transparent",
        subMenuItemBg: "transparent",
        darkItemBg: "transparent",
        darkSubMenuItemBg: "transparent",
        darkPopupBg: p.bgContainer, // collapsed-rail flyout panel
        // Rounded "pill" highlight inset from the rail (Win11 / Teams nav).
        itemBorderRadius: 6,
        itemMarginInline: 8,
        itemHeight: 38,
        itemSelectedBg: p.selectedBg,
        itemSelectedColor: p.primary,
        itemHoverBg: p.hoverBg,
        darkItemSelectedBg: p.selectedBg,
        darkItemSelectedColor: p.primary,
        darkItemHoverBg: p.hoverBg,
        darkItemColor: "rgba(255, 255, 255, 0.88)",
      },
      Card: { borderRadiusLG: 8 },
      Button: { borderRadius: 4, primaryShadow: "none" }, // Fluent flat buttons
    },
  };
}
